import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Avatar from 'flarum/common/components/Avatar';
import Link from 'flarum/common/components/Link';
import username from 'flarum/common/helpers/username';
import humanTime from 'flarum/common/helpers/humanTime';
import HistoryModal from './HistoryModal';
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 700;
function emptyState() {
  return {
    items: [],
    total: 0,
    loading: true,
    loadingMore: false,
    hasMore: false,
    search: '',
    searchTimer: null
  };
}
export default class RequestsModal extends Modal {
  activeTab = 'incoming';
  busyId = null;
  highlighted = null;
  state = (() => ({
    incoming: emptyState(),
    outgoing: emptyState()
  }))();
  className() {
    return 'FriendshipRequestsModal Modal--medium';
  }
  title() {
    return m('[', null, m("i", {
      className: "fas fa-user-clock"
    }), " ", app.translator.trans('forumaker-friendship.forum.requests_modal.title'));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.highlighted = this.attrs.highlightRequestId ?? null;
    this.load('incoming').then(() => this.scrollToHighlighted());
    this.load('outgoing');
  }
  scrollToHighlighted() {
    if (!this.highlighted) return;
    setTimeout(() => {
      this.element?.querySelector?.(`[data-request-id="${this.highlighted}"]`)?.scrollIntoView({
        block: 'center'
      });
    }, 50);
  }
  async load(direction, loadMore) {
    if (loadMore === void 0) {
      loadMore = false;
    }
    const state = this.state[direction];
    if (loadMore) {
      state.loadingMore = true;
    } else {
      state.loading = true;
    }
    m.redraw();
    try {
      const filter = {
        [direction]: this.attrs.user.id()
      };
      if (state.search.trim()) filter.q = state.search.trim();
      const results = await app.store.find('friendship-requests', {
        filter,
        include: 'sender,recipient',
        page: {
          offset: loadMore ? state.items.length : 0,
          limit: PAGE_SIZE
        }
      });
      const newItems = results;
      state.items = loadMore ? [...state.items, ...newItems] : newItems;
      const meta = results.payload?.meta;
      state.total = meta?.page?.total ?? meta?.total ?? state.items.length;
      state.hasMore = state.items.length < state.total;
    } finally {
      state.loadingMore = false;
      state.loading = false;
      m.redraw();
    }
  }
  search(direction, value) {
    const state = this.state[direction];
    state.search = value;
    if (state.searchTimer) window.clearTimeout(state.searchTimer);
    state.searchTimer = window.setTimeout(() => this.load(direction), SEARCH_DEBOUNCE_MS);
  }
  content() {
    const state = this.state[this.activeTab];
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "FriendshipRequestsModal-toolbar"
    }, m("div", {
      className: "FriendshipRequestsModal-tabGroup"
    }, m(Button, {
      className: 'Button FriendshipRequestsModal-tab' + (this.activeTab === 'incoming' ? ' active' : ''),
      onclick: () => this.activeTab = 'incoming'
    }, app.translator.trans('forumaker-friendship.forum.requests_modal.incoming_tab', {
      count: this.state.incoming.total
    })), m(Button, {
      className: 'Button FriendshipRequestsModal-tab' + (this.activeTab === 'outgoing' ? ' active' : ''),
      onclick: () => this.activeTab = 'outgoing'
    }, app.translator.trans('forumaker-friendship.forum.requests_modal.outgoing_tab', {
      count: this.state.outgoing.total
    }))), m("input", {
      type: "text",
      className: "FormControl FriendshipRequestsModal-search",
      placeholder: String(app.translator.trans('forumaker-friendship.forum.requests_modal.search_placeholder')),
      value: state.search,
      oninput: e => this.search(this.activeTab, e.target.value)
    }), m(Button, {
      className: "Button FriendshipRequestsModal-historyBtn",
      icon: "fas fa-history",
      onclick: () => app.modal.show(HistoryModal, {
        user: this.attrs.user
      })
    }, app.translator.trans('forumaker-friendship.forum.requests_modal.history_btn'))), state.loading ? m(LoadingIndicator, null) : this.renderTab(this.activeTab));
  }
  renderTab(direction) {
    const state = this.state[direction];
    return m("div", null, state.items.length === 0 ? m("p", {
      className: "FriendshipRequestsModal-empty"
    }, app.translator.trans(direction === 'incoming' ? 'forumaker-friendship.forum.requests_modal.no_incoming' : 'forumaker-friendship.forum.requests_modal.no_outgoing')) : m("ul", {
      className: "FriendshipRequestsModal-list"
    }, direction === 'incoming' ? this.renderIncomingItems() : this.renderOutgoingItems()), state.hasMore && m("div", {
      className: "FriendshipRequestsModal-loadMore"
    }, m(Button, {
      className: "Button",
      loading: state.loadingMore,
      onclick: () => this.load(direction, true)
    }, app.translator.trans('forumaker-friendship.forum.user.load_more'))));
  }
  renderIncomingItems() {
    return this.state.incoming.items.map(request => {
      const sender = request.sender();
      if (!sender) return null;
      const busy = this.busyId === request.id();
      return m("li", {
        className: "FriendshipRequestsModal-item",
        "data-request-id": request.id(),
        key: request.id()
      }, m(Link, {
        href: app.route.user(sender)
      }, m(Avatar, {
        user: sender
      })), m("div", {
        className: "FriendshipRequestsModal-itemContent"
      }, m(Link, {
        href: app.route.user(sender)
      }, username(sender)), m("span", {
        className: "FriendshipRequestsModal-date"
      }, humanTime(request.createdAt()))), m("div", {
        className: "FriendshipRequestsModal-itemActions"
      }, m(Button, {
        className: "Button Button--primary",
        icon: "fas fa-check",
        loading: busy,
        disabled: busy,
        onclick: () => this.accept(request)
      }, app.translator.trans('forumaker-friendship.forum.requests_modal.accept_btn')), m(Button, {
        className: "Button",
        icon: "fas fa-times",
        disabled: busy,
        onclick: () => this.decline(request)
      }, app.translator.trans('forumaker-friendship.forum.requests_modal.decline_btn'))));
    });
  }
  renderOutgoingItems() {
    return this.state.outgoing.items.map(request => {
      const recipient = request.recipient();
      if (!recipient) return null;
      const busy = this.busyId === request.id();
      return m("li", {
        className: "FriendshipRequestsModal-item",
        key: request.id()
      }, m(Link, {
        href: app.route.user(recipient)
      }, m(Avatar, {
        user: recipient
      })), m("div", {
        className: "FriendshipRequestsModal-itemContent"
      }, m(Link, {
        href: app.route.user(recipient)
      }, username(recipient)), m("span", {
        className: "FriendshipRequestsModal-date"
      }, humanTime(request.createdAt()))), m("div", {
        className: "FriendshipRequestsModal-itemActions"
      }, m(Button, {
        className: "Button",
        icon: "fas fa-ban",
        loading: busy,
        disabled: busy,
        onclick: () => this.cancel(request)
      }, app.translator.trans('forumaker-friendship.forum.requests_modal.cancel_btn'))));
    });
  }
  async accept(request) {
    this.busyId = request.id();
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/friendship-requests/${request.id()}/accept`
      });
      const state = this.state.incoming;
      state.items = state.items.filter(r => r.id() !== request.id());
      state.total = Math.max(0, state.total - 1);

      // No response body carries updated user resources for this endpoint —
      // both sides' friendCount are bumped locally instead.
      const sender = request.sender();
      sender?.pushAttributes({
        friendshipIsFriend: true,
        friendCount: (sender.attribute('friendCount') || 0) + 1
      });
      this.attrs.user.pushAttributes({
        friendCount: (this.attrs.user.attribute('friendCount') || 0) + 1,
        friendshipIncomingRequestCount: Math.max(0, (this.attrs.user.attribute('friendshipIncomingRequestCount') || 0) - 1)
      });
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('forumaker-friendship.forum.requests_modal.accept_success'));
      this.attrs.onChange?.();
    } finally {
      this.busyId = null;
      m.redraw();
    }
  }
  async decline(request) {
    this.busyId = request.id();
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/friendship-requests/${request.id()}/decline`
      });
      const state = this.state.incoming;
      state.items = state.items.filter(r => r.id() !== request.id());
      state.total = Math.max(0, state.total - 1);
      this.attrs.user.pushAttributes({
        friendshipIncomingRequestCount: Math.max(0, (this.attrs.user.attribute('friendshipIncomingRequestCount') || 0) - 1)
      });
      this.attrs.onChange?.();
    } finally {
      this.busyId = null;
      m.redraw();
    }
  }
  async cancel(request) {
    this.busyId = request.id();
    m.redraw();
    try {
      await request.delete();
      const state = this.state.outgoing;
      state.items = state.items.filter(r => r.id() !== request.id());
      state.total = Math.max(0, state.total - 1);
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('forumaker-friendship.forum.requests_modal.cancel_success'));
      this.attrs.onChange?.();
    } finally {
      this.busyId = null;
      m.redraw();
    }
  }
}
flarum.reg.add('forumaker-friendship', 'forum/components/RequestsModal', RequestsModal);